// Types
import type { OrderTestsOperation } from '../../types'

// Libs
import _map from 'lodash/map'
import _reduce from 'lodash/reduce'
import _forEach from 'lodash/forEach'

import { jobsForOrderTests$ } from 'hfty-soil-jobs'

// Operator
import operator from '../operator'

// Controller
import controller from 'hfty-ctrl-orders'

// Selectors
import { userBillingInfo$ } from 'hfty-users'

// Effect Managers
import { call, put, race, select, take } from 'redux-saga/effects'

import { push } from 'react-router-redux'

// Constants
import { PROP_CLIENT_SECRET } from 'hfty-model-orders'

export default function* watchOperations(): Generator<*, void, *> {
  while (true) {
    yield take([operator.actionTypes.add(), operator.actionTypes.edit(), operator.actionTypes.delete()])
    yield call(handleOperations)
  }
}

export function* handleOperations(): Generator<*, void, *> {
  let done = false

  while (!done) {
    const effect = yield race({
      submit: take(operator.actionTypes.submitOperation()),
      cancel: take(operator.actionTypes.cancelOperation()),
    })

    if (effect.submit) {
      yield call(handleSubmitOperation, effect.submit.payload)
    } else {
      done = true
    }
  }
}

export function* handleSubmitOperation(operation) {
  yield call(handleSubmitOrder, operation)
  return false
}

export function* handleSubmitOrder(operation: OrderTestsOperation): Generator {
  const jobs = yield select(jobsForOrderTests$)

  const supplyJobsIds = (supplyId) => {
    if (supplyId) {
      return jobs
        .filter((job) => operation.model.jobs.includes(job.id))
        .filter((job) => job.supplies.map((supply) => supply.supply).includes(supplyId))
        .map((job): number => job.id)
        .join()
    }
  }

  const suppliesObj = _reduce(
    jobs,
    function (acc, nextJob): Array {
      if (operation.model.jobs.indexOf(nextJob.id) > -1) {
        _forEach(nextJob.supplies, (supply) => {
          if (acc[supply.supply]) {
            acc[supply.supply] = {
              supply: supply.supply,
              quantity: acc[supply.supply].quantity + supply.quantity,
              jobs: supplyJobsIds(supply.supply),
            }
          } else {
            acc[supply.supply] = {
              supply: supply.supply,
              quantity: supply.quantity,
              jobs: supplyJobsIds(supply.supply),
            }
          }
        })
      }
      return acc
    },
    {}
  )

  const supplies = _map(suppliesObj, (supply) => {
    return {
      supply: supply.supply,
      quantity: Math.ceil(supply.quantity),
      jobs: supply.jobs,
    }
  })

  const { order } = operation.model
  const newOrder = { ...order }
  if (!order.email) {
    const billingInfo = yield select(userBillingInfo$)
    const email = (billingInfo && billingInfo.email) || ''
    newOrder.email = email
  }

  const { error, response, model } = yield call([controller, controller.create], {
    model: {
      ...newOrder,
      supplies: supplies,
    },
  })

  if (error) {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: false,
      errors: response,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return false
  } else {
    const nextOperation = {
      ...operation,
      model: {
        ...operation.model,
        [PROP_CLIENT_SECRET]: response[PROP_CLIENT_SECRET],
        order: model,
      },
      submitting: false,
      success: false,
      errors: [],
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    yield put(push(`/order-tests/checkout/${response.id}`))

    return false
  }
}
